/* eslint-disable jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Formik } from 'formik';
import axios from 'axios';
import * as Yup from 'yup';

import Block from './Block';
import Button from './Button';
import InputText from './InputText';
import InputCheckbox from './InputCheckbox';
import RichTextHtml from './RichTextHtml';
import H3 from './H3';
import H5 from './H5';

const getValidatorForType = field => {
  switch (field.type) {
    case 'email':
      return Yup.string().email(field.error_format);
    default:
      return Yup.string();
  }
};

const createValidationSchema = fields => {
  const schema = fields.reduce((acc, field) => {
    const validator = getValidatorForType(field);

    return {
      ...acc,
      [field.field_tag]: field.required_field
        ? validator.trim().required(field.error_mandatory)
        : validator.trim(),
    };
  }, {});

  return Yup.object().shape(schema);
};

const STATES = Object.freeze({
  idle: 'idle',
  submitting: 'submitting',
  submitted: 'submitted',
  error: 'error',
});

const MailchimpForm = ({ content }) => {
  const [state, setState] = useState(STATES.idle);
  const [hasConsentError, setHasConsentError] = useState(false);
  const consentAccepted = useRef(null);

  const onSubmit = async (values, { setSubmitting }) => {
    setState(STATES.submitting);

    // If form have consent checkbox, and it's not checked: show error
    if (content.with_consent_checkbox && !consentAccepted.current.checked) {
      setHasConsentError(true);
      setSubmitting(false);
      setState(STATES.idle);

      return;
    }

    try {
      await axios({
        method: 'post',
        url: '/.netlify/functions/sendForm',
        headers: {
          'audience-id': content.audience_id,
        },
        data: {
          email_address: values.EMAIL,
          status: 'subscribed',
          merge_fields: {
            ...values,
          },
        },
      });

      setState(STATES.submitted);
      setSubmitting(false);
    } catch (err) {
      setState(STATES.error);
      setSubmitting(false);
    }
  };

  const initialValues = content.fields.reduce(
    (acc, field) => ({
      ...acc,
      [field.field_tag]: '',
    }),
    {},
  );

  return (
    <Block>
      {(content.title || content.description) && (
        <Block mb="baseUnit">
          {content.title && <H3>{content.title}</H3>}
          {content.description && content.description.html && (
            <RichTextHtml html={content.description.html} />
          )}
        </Block>
      )}

      {state === STATES.submitted ? (
        <Block>
          <RichTextHtml html={content.success_message.html} />
        </Block>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={createValidationSchema(content.fields)}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              {content.fields.map(field => (
                <Block key={`field-${field.field_tag}`} mb="baseUnit">
                  <InputText
                    type={field.type}
                    name={field.field_tag}
                    placeholder={field.placeholder}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values[field.field_tag]}
                    hasError={
                      touched[field.field_tag] && errors[field.field_tag]
                    }
                  />

                  <H5 color="orange">
                    {touched[field.field_tag] && errors[field.field_tag]}
                  </H5>
                </Block>
              ))}

              {state === STATES.error && (
                <Block
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end"
                >
                  <H5 color="orange" mb="baseDown2">
                    {content.error}
                  </H5>

                  <Button onClick={() => setState(STATES.idle)}>
                    {content.try_again}
                  </Button>
                </Block>
              )}

              {content.with_consent_checkbox && (
                <Block mb="baseUnit">
                  <label style={{ display: 'flex' }}>
                    <Block mr="baseDown2">
                      <InputCheckbox
                        ref={consentAccepted}
                        type="checkbox"
                        onChange={() => setHasConsentError(false)}
                      />
                    </Block>

                    <RichTextHtml
                      fontSize="baseDown1"
                      html={content.consent_text.html}
                    />
                  </label>

                  {hasConsentError && (
                    <H5 mb="0" color="orange" isBold>
                      {content.consent_error}
                    </H5>
                  )}
                </Block>
              )}

              {(state === STATES.idle || state === STATES.submitting) && (
                <Block display="flex" justifyContent="flex-end">
                  <Button
                    backgroundColor="blueDarker"
                    color="coralLight"
                    type="submit"
                    disabled={isSubmitting || state === STATES.submitting}
                  >
                    {state === STATES.submitting
                      ? content.submitting
                      : content.submit}
                  </Button>
                </Block>
              )}
            </form>
          )}
        </Formik>
      )}
    </Block>
  );
};

MailchimpForm.propTypes = {
  content: PropTypes.shape({
    audience_id: PropTypes.string.isRequired,
    submit: PropTypes.string.isRequired,
    submitting: PropTypes.string.isRequired,
    cancel: PropTypes.string.isRequired,
    title: PropTypes.string,
    with_consent_checkbox: PropTypes.bool,
    consent_text: PropTypes.shape({
      html: PropTypes.string,
    }),
    consent_error: PropTypes.string,
    description: PropTypes.shape({
      html: PropTypes.string.isRequired,
    }),
    success_message: PropTypes.shape({
      html: PropTypes.string.isRequired,
    }).isRequired,
    error: PropTypes.string,
    try_again: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        error_format: PropTypes.string,
        error_mandatory: PropTypes.string,
        placeholder: PropTypes.string,
        required_field: PropTypes.bool.isRequired,
        type: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
};

export default MailchimpForm;

export const query = graphql`
  fragment MailchimpFormFragment on PrismicMailchimpForm {
    data {
      audience_id
      submit
      submitting
      cancel
      title
      with_consent_checkbox
      consent_error
      consent_text {
        html
      }
      description {
        html
      }
      success_message {
        html
      }
      error
      try_again
      fields {
        field_tag
        label
        error_format
        error_mandatory
        placeholder
        required_field
        type
      }
    }
  }
`;
